import React, { useState, useEffect } from 'react';
import '@googleworkspace/drive-picker-element'
import { extractColors, updateSvgColors, parseStyleString, isValidHexColor, SVG_SHAPES } from './utils/svgUtils';
import { trackEvent, GTM_EVENTS } from './utils/gtmUtils';
import {
  gisLoaded,
  signIn,
  isSignedIn,
  readSvgFile,
  saveSvgFile,
  signOut,
  getUserEmail
} from './utils/driveGapi';
import { parse } from 'svgson';
import './App.css';
import { FiTrash, FiSave, FiSave as FiSaveAs, FiDownload, FiRotateCcw, FiRotateCw, FiRefreshCw, FiUpload } from 'react-icons/fi';
import { FiTrendingUp, FiCircle, FiActivity } from 'react-icons/fi';
import { MdCropSquare, MdPanoramaHorizontal, MdChangeHistory } from 'react-icons/md';
import { BsFillPaletteFill } from 'react-icons/bs';
import { TbWaveSine } from 'react-icons/tb';
import ElementPanel from './components/ElementPanel';
import Header from './components/Header';
import FeedbackBanner from './components/FeedbackBanner';
import SvgPreview from './components/SvgPreview';
import AdsSidebar from './components/AdsSidebar';
import Footer from './components/Footer';
import { mapSvgAttributes } from './utils/svgAttrMap';
import Modal from './components/SaveExportModal.jsx';

const SVG_COMPLEXITY_THRESHOLD = 1000;

function autoFitSvgViewBox(svgString) {
  try {
    const container = document.createElement('div');
    container.style.visibility = 'hidden';
    container.style.position = 'absolute';
    container.style.width = '0';
    container.style.height = '0';
    document.body.appendChild(container);

    container.innerHTML = svgString;
    const svg = container.querySelector('svg');
    if (!svg) {
      document.body.removeChild(container);
      return svgString;
    }

    svg.removeAttribute('width');
    svg.removeAttribute('height');
    svg.setAttribute('width', '100%');
    svg.setAttribute('height', '100%');

    const bbox = svg.getBBox();
    svg.setAttribute('viewBox', `${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`);

    const result = svg.outerHTML;
    document.body.removeChild(container);
    return result;
  } catch (e) {
    return svgString;
  }
}

function countSvgElements(node) {
  let count = 1;
  if (node.children) {
    for (const child of node.children) {
      count += countSvgElements(child);
    }
  }
  return count;
}

// Utility to flatten SVG elements for sidebar
function flattenSvgElements(node, elements = [], parentId = null) {
  const { name, attributes, children, value } = node;
  const isShape = SVG_SHAPES.includes(name);
  const id = attributes['data-svgtoolbox-id'] || attributes?.id || `${name}-${elements.length}`;

  // Enhanced color/type detection
  let color = null;
  let type = 'solid';
  if (attributes.fill && attributes.fill.startsWith('url(')) {
    color = 'gradient';
    type = 'gradient';
  } else if (attributes.stroke && attributes.stroke.startsWith('url(')) {
    color = 'gradient';
    type = 'gradient';
  } else if (attributes.fill && !attributes.fill.startsWith('#') && !attributes.fill.startsWith('rgb') && attributes.fill !== 'none') {
    color = 'css';
    type = 'css';
  } else if (attributes.stroke && !attributes.stroke.startsWith('#') && !attributes.stroke.startsWith('rgb') && attributes.stroke !== 'none') {
    color = 'css';
    type = 'css';
  } else {
    color = attributes.fill || attributes.stroke || '#000';
  }

  if (isShape) {
    elements.push({
      id,
      name,
      color,
      type,
      attributes,
      parentId,
      value, // Include the text content for text elements
    });
  }
  if (children) {
    children.forEach(child => flattenSvgElements(child, elements, id));
  }
  return elements;
}

// ErrorBoundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true };
  }
  componentDidCatch(error, errorInfo) {
    // You can log error info here if needed
  }
  render() {
    if (this.state.hasError) {
      return <div className="error-message">An error occurred while rendering the SVG. Displaying as a static image.</div>;
    }
    return this.props.children;
  }
}

function renderSvgNode(node, { hoveredId, setHoveredId, selectedId, setSelectedId, setSelectedElement, pathIndex = 0, isRoot = false }) {
  if (!node || !node.name) return null; // Defensive: skip invalid nodes
  const { name, attributes, children } = node;
  const isShape = SVG_SHAPES.includes(name);
  const id = attributes['data-svgtoolbox-id'] || attributes.id || `${name}-${pathIndex}`;
  
  // Parse style string to object if needed
  const styleProp = attributes.style
    ? (typeof attributes.style === 'string'
        ? parseStyleString(attributes.style)
        : attributes.style)
    : undefined;

  const colorAttr = attributes.fill || attributes.stroke;
  const isSelectedShape = selectedId === id && isShape;
  const outline = (!isSelectedShape && hoveredId === id && isShape)
    ? { stroke: '#007bff', strokeWidth: 3, filter: 'drop-shadow(0 0 2px #007bff)' }
    : {};

  // Only attach selection/hover handlers to shapes, not the root <svg>
  const elementProps = {
    key: id,
    ...mapSvgAttributes(attributes),
    ...(styleProp ? { style: styleProp } : {}),
    ...(isShape ? { ...outline } : {}),
    ...(isShape ? {
      onMouseOver: () => setHoveredId(id),
      onMouseOut: () => setHoveredId(null),
      onClick: (e) => {
        e.stopPropagation();
        console.log('Element clicked:', { id, name, attributes });
        setSelectedId(id);
        setSelectedElement({ id, name, color: colorAttr, attributes });
      }
    } : {}),
    ...(isSelectedShape ? { className: (attributes.className ? attributes.className + ' marching-ants' : 'marching-ants') } : {})
  };

  // Suppress original stroke/strokeWidth for marching ants, but preserve in data-original-* attributes
  if (isSelectedShape) {
    if ('stroke' in elementProps) elementProps['data-original-stroke'] = elementProps.stroke;
    if ('strokeWidth' in elementProps) elementProps['data-original-stroke-width'] = elementProps.strokeWidth;
    delete elementProps.stroke;
    delete elementProps.strokeWidth;
    delete elementProps.filter;
  }

  return React.createElement(
    name,
    elementProps,
    children && children.map((child, i) =>
      renderSvgNode(child, { hoveredId, setHoveredId, selectedId, setSelectedId, setSelectedElement, pathIndex: i, isRoot: false })
    )
  );
}

// Add this new function before the App component
function assignSvgIds(node, pathIndex = 0) {
  if (!node) return;
  
  // Skip the root svg element
  if (node.name !== 'svg') {
    if (!node.attributes) {
      node.attributes = {};
    }
    // Only assign ID if it doesn't already have one
    if (!node.attributes['data-svgtoolbox-id'] && !node.attributes.id) {
      node.attributes['data-svgtoolbox-id'] = `${node.name}-${pathIndex}`;
    }
  }
  
  if (node.children) {
    node.children.forEach((child, index) => {
      assignSvgIds(child, index);
    });
  }
  
  return node;
}

// Utility to get file ID from the 'state' param
function getFileIdFromStateParam() {
  const params = new URLSearchParams(window.location.search);
  if (params.has('state')) {
    try {
      const state = JSON.parse(params.get('state'));
      if (state.ids && state.ids.length > 0) {
        return state.ids[0];
      }
    } catch (e) {
      console.error('Failed to parse state param:', e);
    }
  }
  return null;
}

const shapeIcons = {
  path: <FiTrendingUp />,
  rect: <MdCropSquare />,
  circle: <FiCircle />,
  ellipse: <MdPanoramaHorizontal />,
  polygon: <MdChangeHistory />,
  polyline: <FiActivity />,
  gradient: <TbWaveSine style={{ color: 'linear-gradient(90deg, #facc15, #2563eb)' }} />,
  css: <BsFillPaletteFill style={{ color: '#888' }} />,
};

// Utility to build a tree structure for the sidebar, preserving groups
function buildElementTree(node, parentId = null) {
  const { name, attributes = {}, children = [], value } = node;
  const id = attributes['data-svgtoolbox-id'] || attributes.id || `${name}-${Math.random().toString(36).slice(2,8)}`;
  const isGroup = name === 'g';
  const isShape = SVG_SHAPES.includes(name);

  // Parse style string if present
  let styleObj = {};
  if (attributes.style) {
    styleObj = typeof attributes.style === 'string' ? parseStyleString(attributes.style) : attributes.style;
  }

  // Enhanced color/type detection
  let color = null;
  let type = 'solid';
  let fill = attributes.fill || styleObj.fill;
  let stroke = attributes.stroke || styleObj.stroke;
  if (fill && fill.startsWith('url(')) {
    color = 'gradient';
    type = 'gradient';
  } else if (stroke && stroke.startsWith('url(')) {
    color = 'gradient';
    type = 'gradient';
  } else if (fill && !fill.startsWith('#') && !fill.startsWith('rgb') && fill !== 'none') {
    color = 'css';
    type = 'css';
  } else if (stroke && !stroke.startsWith('#') && !stroke.startsWith('rgb') && stroke !== 'none') {
    color = 'css';
    type = 'css';
  } else if (fill && fill !== 'none') {
    color = fill;
  } else if (stroke && stroke !== 'none') {
    color = stroke;
  } else {
    color = '#000';
  }

  if (isGroup) {
    return {
      id,
      name,
      type: 'group',
      attributes,
      parentId,
      children: children.map(child => buildElementTree(child, id)),
    };
  } else if (isShape) {
    return {
      id,
      name,
      color,
      type,
      attributes,
      parentId,
      fill,
      stroke,
      value, // Include the text content for text elements
      children: [],
    };
  } else if (name === 'svg' || children.length > 0) {
    // For non-group, non-shape nodes with children (e.g. <svg>), recurse
    return {
      id,
      name,
      type: 'container',
      attributes,
      parentId,
      children: children.map(child => buildElementTree(child, id)),
    };
  }
  return null;
}

// Utility to get fill/stroke from attributes and style
function getFillStroke(attributes) {
  let styleObj = {};
  if (attributes.style) {
    styleObj = typeof attributes.style === 'string' ? parseStyleString(attributes.style) : attributes.style;
  }
  return {
    fill: attributes.fill || styleObj.fill,
    stroke: attributes.stroke || styleObj.stroke
  };
}

function App() {
  const [svgContent, setSvgContent] = useState('');
  const [colors, setColors] = useState([]);
  const [previewSvg, setPreviewSvg] = useState('');
  const [svgJson, setSvgJson] = useState(null);
  const [isComplex, setIsComplex] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fileName, setFileName] = useState('');
  const [hoveredId, setHoveredId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedElement, setSelectedElement] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [originalSvg, setOriginalSvg] = useState('');
  const [lastSavedPath, setLastSavedPath] = useState(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveLocation, setSaveLocation] = useState('drive'); // 'local' or 'drive'
  const [googleApiReady, setGoogleApiReady] = useState(false);
  const [elementTree, setElementTree] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [fileOrigin, setFileOrigin] = useState(null); // 'drive' or 'local'
  const [driveFileId, setDriveFileId] = useState(null);
  const [originalFileName, setOriginalFileName] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [pendingShowSaveDialog, setPendingShowSaveDialog] = useState(false);
  const [pickerMode, setPickerMode] = useState(null); // 'open' or 'save'

  // Ref for hidden file input
  const fileInputRef = React.useRef();
  const pickerRef = React.useRef(null);

  const addToHistory = (newSvgContent) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newSvgContent);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const previousSvg = history[newIndex];
      setSvgContent(previousSvg);
      setPreviewSvg(autoFitSvgViewBox(previousSvg));
      setHistoryIndex(newIndex);
      setSelectedId(null);
      setSelectedElement(null);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const nextSvg = history[newIndex];
      setSvgContent(nextSvg);
      setPreviewSvg(autoFitSvgViewBox(nextSvg));
      setHistoryIndex(newIndex);
      setSelectedId(null);
      setSelectedElement(null);
    }
  };

  const handleReset = () => {
    if (originalSvg) {
      setSvgContent(originalSvg);
      setPreviewSvg(autoFitSvgViewBox(originalSvg));
      setHistory([originalSvg]);
      setHistoryIndex(0);
      setSelectedId(null);
      setSelectedElement(null);
    }
  };

  useEffect(() => {
    // Wait for Google APIs to load before enabling the button
    const checkGisLoaded = () => {
      if (window.google && window.google.accounts && window.google.picker) {
        gisLoaded(setAccessToken);
        setGoogleApiReady(true);
      } else {
        setTimeout(checkGisLoaded, 200);
      }
    };
    checkGisLoaded();
  }, []);

  useEffect(() => {
    // Handle Google Drive 'Open With' file loading
    const fileId = getFileIdFromStateParam();
    if (fileId) {
      (async () => {
        try {
          setIsLoading(true);
          setError(null);
          if (!isSignedIn(accessToken)) {
            await signIn(setAccessToken);
          }
          const content = await readSvgFile({ id: fileId, name: `${fileId}.svg` }, accessToken);
          setOriginalSvg(content);
          setSvgContent(content);
          setPreviewSvg(autoFitSvgViewBox(content));
          const extractedColors = extractColors(content);
          setColors(extractedColors);
          setFileName(`${fileId}.svg`);
          setOriginalFileName(`${fileId}.svg`);
          setDriveFileId(fileId);
          setFileOrigin('drive');
          setHistory([content]);
          setHistoryIndex(0);
          setSelectedId(null);
          setSelectedElement(null);
          
          trackEvent(GTM_EVENTS.GOOGLE_DRIVE_OPEN, {
            file_name: `${fileId}.svg`,
            file_id: fileId
          });
        } catch (error) {
          setError('Error loading file from Google Drive: ' + error.message);
        } finally {
          setIsLoading(false);
        }
      })();
    }
  }, [accessToken]);

  useEffect(() => {
    if (previewSvg) {
      parse(previewSvg).then(json => {
        console.log('Parsed SVG JSON before ID assignment:', json);
        // Assign IDs to all elements
        const jsonWithIds = assignSvgIds(json);
        console.log('Parsed SVG JSON after ID assignment:', jsonWithIds);
        
        const count = countSvgElements(jsonWithIds);
        setIsComplex(count > SVG_COMPLEXITY_THRESHOLD);
        setSvgJson(jsonWithIds);
      });
    } else {
      setSvgJson(null);
      setIsComplex(false);
    }
  }, [previewSvg]);

  // Update svgJson and element list when previewSvg changes
  const [elementList, setElementList] = useState([]);
  useEffect(() => {
    if (svgJson) {
      const elements = flattenSvgElements(svgJson);
      console.log('Flattened SVG elements:', elements);
      setElementList(elements);
    } else {
      setElementList([]);
    }
  }, [svgJson]);

  // When an element is selected, update selectedElement
  useEffect(() => {
    if (selectedId && elementTree) {
      // Find the selected node in the tree
      function findNode(node) {
        if (!node) return null;
        if (node.id === selectedId) return node;
        if (node.children) {
          for (const child of node.children) {
            const found = findNode(child);
            if (found) return found;
          }
        }
        return null;
      }
      const node = findNode(elementTree);
      if (node) {
        const { fill, stroke } = getFillStroke(node.attributes);
        setSelectedElement({ ...node, fill, stroke });
      } else {
        setSelectedElement(null);
      }
    } else {
      setSelectedElement(null);
    }
  }, [selectedId, elementTree]);

  // Track page view on mount
  useEffect(() => {
    trackEvent(GTM_EVENTS.PAGE_VIEW, {
      page_path: window.location.pathname,
      page_title: document.title
    });
  }, []);

  // Consolidated Open from Google Drive button
  const handleOpenFromDrive = async () => {
    if (!isSignedIn(accessToken)) {
      await signIn(setAccessToken);
    }
    
    // Try to get user email, but don't block the picker if it fails
    try {
      const email = await getUserEmail(accessToken);
      if (email) {
        console.log('User signed in with email:', email);
      }
    } catch (error) {
      console.warn('Could not fetch user email:', error);
      // Continue with picker even if email fetch fails
    }
    
    setPickerMode('open');
    setShowPicker(true);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        setIsLoading(true);
        setError(null);
        const content = await file.text();
        setSvgContent(content);
        setOriginalSvg(content);
        setFileName(file.name);
        setOriginalFileName(file.name);
        setDriveFileId(null);
        setFileOrigin('local');
        setPreviewSvg(autoFitSvgViewBox(content));
        addToHistory(content);
        setSelectedId(null);
        setSelectedElement(null);
        const svgJson = await parse(content);
        const elementCount = countSvgElements(svgJson);
        const complexityLevel = elementCount > SVG_COMPLEXITY_THRESHOLD ? 'complex' : 'simple';
        trackEvent(GTM_EVENTS.FILE_UPLOAD, {
          element_count: elementCount,
          complexity_level: complexityLevel,
          file_size: file.size
        });
      } catch (err) {
        setError('Error reading file: ' + err.message);
        trackEvent(GTM_EVENTS.ERROR_OCCURRED, {
          error_type: 'file_upload',
          error_message: err.message
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Unified Save/Export button handler
  const handleSaveExportClick = () => {
    setPendingShowSaveDialog(true);
  };

  useEffect(() => {
    if (pendingShowSaveDialog && fileOrigin) {
      setShowSaveDialog(true);
      setPendingShowSaveDialog(false);
    }
  }, [pendingShowSaveDialog, fileOrigin]);

  // Update saveLocation default based on fileOrigin when opening modal
  useEffect(() => {
    if (showSaveDialog) {
      setSaveLocation(fileOrigin === 'drive' ? 'drive' : 'local');
    }
  }, [showSaveDialog, fileOrigin]);

  // Unified Save/Export logic
  const handleSaveExport = async ({ fileName: newFileName, saveLocation: newSaveLocation, saveMode }) => {
    setShowSaveDialog(false);
    setIsLoading(true);
    setError(null);
    try {
      if (newSaveLocation === 'drive') {
        // Ensure user is signed in before saving to Drive
        if (!isSignedIn(accessToken)) {
          await signIn(setAccessToken);
        }
        if (saveMode === 'overwrite') {
          // Overwrite existing file
          await saveSvgFile(svgContent, driveFileId, true, null, accessToken);
          trackEvent(GTM_EVENTS.FILE_SAVE, {
            save_type: 'overwrite',
            file_name: newFileName
          });
        } else if (saveMode === 'drive-new') {
          // Show picker for folder selection
          setPickerMode('save');
          setShowPicker(true);
          const pickFolder = new Promise((resolve, reject) => {
            const handleFolderPicked = (event) => {
              const files = event.detail.docs;
              if (files && files.length > 0) {
                const folder = files[0];
                if (folder.mimeType === 'application/vnd.google-apps.folder') {
                  resolve(folder.id);
                } else {
                  reject(new Error('Please select a folder'));
                }
              } else {
                reject(new Error('No folder selected'));
              }
              pickerRef.current.removeEventListener('picker-picked', handleFolderPicked);
              pickerRef.current.removeEventListener('picker-canceled', handleCanceled);
            };

            const handleCanceled = () => {
              resolve(null); // Resolve with null to indicate cancellation
              pickerRef.current.removeEventListener('picker-picked', handleFolderPicked);
              pickerRef.current.removeEventListener('picker-canceled', handleCanceled);
            };

            // Wait for picker to be initialized
            const checkPicker = setInterval(() => {
              if (pickerRef.current) {
                clearInterval(checkPicker);
                pickerRef.current.addEventListener('picker-picked', handleFolderPicked);
                pickerRef.current.addEventListener('picker-canceled', handleCanceled);
              }
            }, 100);

            // Cleanup interval after 5 seconds if picker never initializes
            setTimeout(() => clearInterval(checkPicker), 5000);
          });

          try {
            const folderId = await pickFolder;
            if (folderId === null) {
              // User cancelled folder selection
              setIsLoading(false);
              return;
            }
            // Save as new file in Drive
            await saveSvgFile(svgContent, newFileName, false, folderId, accessToken);
            trackEvent(GTM_EVENTS.FILE_SAVE, {
              save_type: 'new_file',
              file_name: newFileName,
              folder_id: folderId
            });
          } catch (error) {
            if (error.message === 'Please select a folder' || error.message === 'No folder selected') {
              // User didn't select a folder properly
              setIsLoading(false);
              return;
            }
            throw error; // Re-throw other errors
          }
        }
      } else {
        // Save locally (download)
        const blob = new Blob([svgContent], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = newFileName || 'exported.svg';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        trackEvent(GTM_EVENTS.FILE_EXPORT, {
          file_name: newFileName || 'exported.svg'
        });
      }
      setFileName(newFileName);
      setSaveLocation(newSaveLocation);
    } catch (err) {
      setError('Error saving file: ' + err.message);
      trackEvent(GTM_EVENTS.ERROR_OCCURRED, {
        error_type: 'file_save_export',
        error_message: err.message
      });
    } finally {
      setIsLoading(false);
      setShowPicker(false);
      setPickerMode(null);
    }
  };

  const handleElementColorChange = (elementId, colorType, newColor) => {
    try {
      // Update svgJson directly
      const newSvgJson = JSON.parse(JSON.stringify(svgJson));
      function updateColor(node) {
        if (node.attributes && (node.attributes['data-svgtoolbox-id'] === elementId || node.attributes.id === elementId)) {
          node.attributes[colorType] = newColor;
        }
        if (node.children) node.children.forEach(updateColor);
      }
      updateColor(newSvgJson);
      setSvgJson(newSvgJson);

      // Update svgContent and previewSvg from the updated svgJson
      import('svgson').then(({ stringify }) => {
        const svgStr = stringify(newSvgJson);
        setSvgContent(svgStr);
        setPreviewSvg(autoFitSvgViewBox(svgStr));
        addToHistory(svgStr);
      });

      trackEvent(GTM_EVENTS.COLOR_MODIFY, {
        element_id: elementId,
        color_type: colorType,
        new_color: newColor
      });
    } catch (err) {
      setError('Error updating color: ' + err.message);
      trackEvent(GTM_EVENTS.ERROR_OCCURRED, {
        error_type: 'color_modify',
        error_message: err.message
      });
    }
  };

  const handleDeleteElement = () => {
    if (!selectedId || !svgJson) return;
    
    console.log('Attempting to delete element with ID:', selectedId);
    console.log('Current SVG JSON:', svgJson);
    
    function deleteElement(node, pathIndex = 0) {
      if (!node.children) return;
      
      console.log('Checking node:', node.name, 'at index:', pathIndex);
      
      // Find the index of the element to delete
      const indexToDelete = node.children.findIndex(child => {
        const childId = child.attributes?.['data-svgtoolbox-id'] || 
                       child.attributes?.id || 
                       `${child.name}-${pathIndex}`;
        console.log('Checking child:', child.name, 'with ID:', childId);
        return childId === selectedId;
      });
      
      if (indexToDelete !== -1) {
        console.log('Found element to delete at index:', indexToDelete);
        // Remove the element
        node.children.splice(indexToDelete, 1);
        return true;
      }
      
      // If not found in this level, search in children
      for (let i = 0; i < node.children.length; i++) {
        if (deleteElement(node.children[i], i)) {
          return true;
        }
      }
      
      return false;
    }

    const newSvgJson = JSON.parse(JSON.stringify(svgJson));
    const wasDeleted = deleteElement(newSvgJson);

    console.log('Was element deleted?', wasDeleted);

    if (wasDeleted) {
      import('svgson').then(({ stringify }) => {
        const svgStr = stringify(newSvgJson);
        console.log('Updated SVG after deletion:', svgStr);
        
        // Update all necessary state
        setSvgContent(svgStr);
        setPreviewSvg(autoFitSvgViewBox(svgStr));
        setSvgJson(newSvgJson);
        setSelectedId(null);
        setSelectedElement(null);
        addToHistory(svgStr);
        
        trackEvent(GTM_EVENTS.ELEMENT_DELETE, {
          element_id: selectedId
        });
      });
    } else {
      console.log('Failed to delete element - not found in SVG tree');
    }
  };

  const handleFilePickerClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  useEffect(() => {
    if (svgJson) {
      // If the root is <svg>, use its children as top-level nodes
      if (svgJson.name === 'svg' && Array.isArray(svgJson.children)) {
        setElementTree({
          id: 'root',
          name: 'root',
          type: 'container',
          children: svgJson.children.map(child => buildElementTree(child, null)).filter(Boolean)
        });
      } else {
        setElementTree(buildElementTree(svgJson));
      }
    } else {
      setElementTree(null);
    }
  }, [svgJson]);

  // Handler for rect attribute changes
  const handleRectAttributeChange = (attr, value, node) => {
    if (!svgJson || node.name !== 'rect') return;
    // Validate value (should be a number)
    const numValue = Number(value);
    if (isNaN(numValue)) return;

    // Find and update the rect in the SVG JSON
    const newSvgJson = JSON.parse(JSON.stringify(svgJson));
    function updateRect(nodeToUpdate) {
      if (nodeToUpdate.attributes && nodeToUpdate.attributes['data-svgtoolbox-id'] === node.attributes['data-svgtoolbox-id']) {
        nodeToUpdate.attributes[attr] = numValue;
      }
      if (nodeToUpdate.children) nodeToUpdate.children.forEach(updateRect);
    }
    updateRect(newSvgJson);

    import('svgson').then(({ stringify }) => {
      try {
        const svgStr = stringify(newSvgJson);
        setSvgContent(svgStr);
        setPreviewSvg(autoFitSvgViewBox(svgStr));
        setSvgJson(newSvgJson);
      } catch (error) {
        console.error('Error stringifying SVG:', error);
      }
    });
  };

  // Handler for circle attribute changes
  const handleCircleAttributeChange = (attr, value, node) => {
    if (!svgJson || node.name !== 'circle') return;
    // Validate value (should be a number)
    const numValue = Number(value);
    if (isNaN(numValue)) return;

    // Find and update the circle in the SVG JSON
    const newSvgJson = JSON.parse(JSON.stringify(svgJson));
    function updateCircle(nodeToUpdate) {
      if (nodeToUpdate.attributes && nodeToUpdate.attributes['data-svgtoolbox-id'] === node.attributes['data-svgtoolbox-id']) {
        nodeToUpdate.attributes[attr] = numValue;
      }
      if (nodeToUpdate.children) nodeToUpdate.children.forEach(updateCircle);
    }
    updateCircle(newSvgJson);

    import('svgson').then(({ stringify }) => {
      try {
        const svgStr = stringify(newSvgJson);
        setSvgContent(svgStr);
        setPreviewSvg(autoFitSvgViewBox(svgStr));
        setSvgJson(newSvgJson);
      } catch (error) {
        console.error('Error stringifying SVG:', error);
      }
    });
  };

  // Handler for ellipse attribute changes
  const handleEllipseAttributeChange = (attr, value, node) => {
    if (!svgJson || node.name !== 'ellipse') return;
    // Validate value (should be a number)
    const numValue = Number(value);
    if (isNaN(numValue)) return;

    // Find and update the ellipse in the SVG JSON
    const newSvgJson = JSON.parse(JSON.stringify(svgJson));
    function updateEllipse(nodeToUpdate) {
      if (nodeToUpdate.attributes && nodeToUpdate.attributes['data-svgtoolbox-id'] === node.attributes['data-svgtoolbox-id']) {
        nodeToUpdate.attributes[attr] = numValue;
      }
      if (nodeToUpdate.children) nodeToUpdate.children.forEach(updateEllipse);
    }
    updateEllipse(newSvgJson);

    import('svgson').then(({ stringify }) => {
      try {
        const svgStr = stringify(newSvgJson);
        setSvgContent(svgStr);
        setPreviewSvg(autoFitSvgViewBox(svgStr));
        setSvgJson(newSvgJson);
      } catch (error) {
        console.error('Error stringifying SVG:', error);
      }
    });
  };

  // Handler for stroke width changes
  const handleStrokeWidthChange = (value, node) => {
    if (!svgJson) return;
    // Validate value (should be a number)
    const numValue = Number(value);
    if (isNaN(numValue)) return;

    // Find and update the element in the SVG JSON
    const newSvgJson = JSON.parse(JSON.stringify(svgJson));
    function updateStrokeWidth(nodeToUpdate) {
      if (nodeToUpdate.attributes && nodeToUpdate.attributes['data-svgtoolbox-id'] === node.attributes['data-svgtoolbox-id']) {
        nodeToUpdate.attributes['stroke-width'] = numValue;
      }
      if (nodeToUpdate.children) nodeToUpdate.children.forEach(updateStrokeWidth);
    }
    updateStrokeWidth(newSvgJson);

    import('svgson').then(({ stringify }) => {
      try {
        const svgStr = stringify(newSvgJson);
        setSvgContent(svgStr);
        setPreviewSvg(autoFitSvgViewBox(svgStr));
        setSvgJson(newSvgJson);
      } catch (error) {
        console.error('Error stringifying SVG:', error);
      }
    });
  };

  // Handler for when a file is picked
  const handlePickerPicked = async (event) => {
    console.log('Picker picked event:', event.detail);
    const files = event.detail.docs;
    if (files && files.length > 0) {
      const file = files[0];
      console.log('Selected file:', file);
      
      // If we're in save mode, handle folder selection
      if (pickerMode === 'save') {
        if (file.mimeType === 'application/vnd.google-apps.folder') {
          return file.id;
        } else {
          throw new Error('Please select a folder');
        }
      }
      
      // Otherwise handle file opening
      try {
        setIsLoading(true);
        setError(null);
        // Always use your own accessToken for API calls
        const content = await readSvgFile({ id: file.id, name: file.name }, accessToken);
        console.log('Loaded SVG content:', content);
        setOriginalSvg(content);
        setSvgContent(content);
        setPreviewSvg(autoFitSvgViewBox(content));
        const extractedColors = extractColors(content);
        setColors(extractedColors);
        setFileName(file.name);
        setOriginalFileName(file.name);
        setDriveFileId(file.id);
        setFileOrigin('drive');
        setHistory([content]);
        setHistoryIndex(0);
        setSelectedId(null);
        setSelectedElement(null);
        trackEvent(GTM_EVENTS.GOOGLE_DRIVE_OPEN, {
          file_name: file.name,
          file_id: file.id
        });
      } catch (error) {
        console.error('Error loading file from Google Drive:', error);
        setError('Error loading file from Google Drive: ' + error.message);
      } finally {
        setIsLoading(false);
        setShowPicker(false);
        setPickerMode(null);
      }
    } else {
      setShowPicker(false);
      setPickerMode(null);
    }
  };

  // Handler for when the picker is canceled
  const handlePickerCanceled = () => {
    setShowPicker(false);
    setPickerMode(null);
  };

  useEffect(() => {
    const picker = pickerRef.current;
    if (!picker) return;

    const handlePicked = (event) => handlePickerPicked(event);
    const handleCanceled = (event) => handlePickerCanceled(event);

    picker.addEventListener('picker-picked', handlePicked);
    picker.addEventListener('picker-canceled', handleCanceled);

    return () => {
      picker.removeEventListener('picker-picked', handlePicked);
      picker.removeEventListener('picker-canceled', handleCanceled);
    };
  }, [showPicker]);

  const handleSignOut = () => {
    signOut(setAccessToken);
    setSvgContent('');
    setOriginalSvg('');
    setFileName('');
    setOriginalFileName('');
    setDriveFileId(null);
    setFileOrigin(null);
    setHistory([]);
    setHistoryIndex(-1);
    setPreviewSvg('');
    setSvgJson(null);
    setElementTree(null);
    setExpandedGroups({});
    setSelectedId(null);
    setSelectedElement(null);
    setColors([]);
    setError(null);
    setIsComplex(false);
    setLastSavedPath(null);
  };

  return (
    <div className="app">
      <FeedbackBanner feedbackUrl="https://docs.google.com/forms/d/e/1FAIpQLScGGgq1qNXx9CwVmuAWOBRcm5vJCO8jJ3EIPGUgeKxA83xHfw/viewform?usp=dialog" />
      <Header
        handleOpenFromDrive={handleOpenFromDrive}
        isLoading={isLoading}
        googleApiReady={googleApiReady}
        handleFilePickerClick={handleFilePickerClick}
        handleSave={handleSaveExportClick}
        svgContent={svgContent}
        historyIndex={historyIndex}
        history={history}
        handleUndo={handleUndo}
        handleRedo={handleRedo}
        originalSvg={originalSvg}
        fileInputRef={fileInputRef}
        handleFileUpload={handleFileUpload}
        handleReset={handleReset}
        isSignedIn={isSignedIn(accessToken)}
        handleSignOut={handleSignOut}
      />

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {showSaveDialog && (
        <Modal
          key={fileOrigin + '-' + fileName + '-' + showSaveDialog}
          fileName={fileName}
          saveLocation={saveLocation}
          setSaveLocation={setSaveLocation}
          onSave={handleSaveExport}
          onCancel={() => setShowSaveDialog(false)}
          originalFileName={originalFileName}
          fileOrigin={fileOrigin}
        />
      )}

      <main>
        <SvgPreview
          previewSvg={previewSvg}
          svgJson={svgJson}
          isComplex={isComplex}
          hoveredId={hoveredId}
          setHoveredId={setHoveredId}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          setSelectedElement={setSelectedElement}
          error={error}
          loading={isLoading}
        />
        <ElementPanel
          elementTree={elementTree}
          selectedElement={selectedElement}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          setSelectedElement={setSelectedElement}
          hoveredId={hoveredId}
          setHoveredId={setHoveredId}
          expandedGroups={expandedGroups}
          setExpandedGroups={setExpandedGroups}
          handleElementColorChange={handleElementColorChange}
          handleDeleteElement={handleDeleteElement}
          handleRectAttributeChange={handleRectAttributeChange}
          handleCircleAttributeChange={handleCircleAttributeChange}
          handleEllipseAttributeChange={handleEllipseAttributeChange}
          handleStrokeWidthChange={handleStrokeWidthChange}
        />
        {/* <AdsSidebar /> */}
      </main>
      <Footer />
      {showPicker && (
        <drive-picker
          ref={pickerRef}
          client-id="916730590504-63sr99tj6dhpdt8j9vkvechml3j66b4n.apps.googleusercontent.com"
          developer-key="AIzaSyBHimwpJFaS4xn23i6zwbPeBhU25napWvQ"
          scopes="https://www.googleapis.com/auth/drive.file"
          multiselect="false"
          upload
          visible={true}
          oauth-token={accessToken}
        >
          <drive-picker-docs-view
            view-id={pickerMode === 'save' ? "FOLDERS" : "DOCS"}
            mime-types={pickerMode === 'save' ? undefined : "image/svg+xml"}
            include-folders="true"
            select-folder-enabled={pickerMode === 'save'}
            mode="LIST"
          />
        </drive-picker>
      )}
    </div>
  );
}

export default App; 